/**
 * `Badge` (W1-12 ui-kit-i18n; contract BadgeProps, DESIGN.md §2.4, §4.11): a small chip with an icon AND a word
 * (colour is never the only signal). Kinds: in use, Phira ignores, RPE >= 1.7, editor only, baked on export,
 * Full, and checker problems (severity icon + colour). Every badge has a tooltip explaining it.
 *  - With `onClick` (problem badges open Problems) it is a button and a tab stop.
 *  - Otherwise it is a status chip that is NOT a tab stop (an inspector row with several badges would add as
 *    many Tab presses per field). Keyboard and screen-reader users get the explanation from the field instead:
 *    `BadgeList` with a `descriptionId` renders the explanations in a hidden element that Field and NumberField
 *    reference from aria-describedby. Hovering still shows the tooltip.
 */

import type { IconName } from '../app/registry/types';
import { assertNever } from '../core/util/assert';
import type { MessageRef } from '../core/util/types';
import { useT } from '../i18n/useT';
import type { MessageKey } from '../i18n/types';
import s from './Badge.module.css';
import { useTr } from './bridge';
import { Icon } from './Icon';
import { cx } from './internal/cx';
import { keepFocusOnPointerDown } from './internal/focus';
import { Tooltip } from './Tooltip';
import type { BadgeKind, BadgeProps } from './types';
import { m } from './ui.messages';

type Severity = NonNullable<BadgeProps['severity']>;

export const SEVERITY_ICON: Readonly<Record<Severity, IconName>> = {
  fatal: 'OctagonX',
  error: 'CircleX',
  warning: 'TriangleAlert',
  tip: 'Lightbulb',
};

const SEVERITY_LABEL: Readonly<Record<Severity, MessageKey>> = {
  fatal: m['severity.fatal'],
  error: m['severity.error'],
  warning: m['severity.warning'],
  tip: m['severity.tip'],
};

interface KindInfo {
  readonly icon: IconName;
  readonly label: MessageKey;
  readonly tooltip: MessageKey;
}

export function badgeKindInfo(kind: BadgeKind, severity: Severity = 'warning'): KindInfo {
  switch (kind) {
    case 'inUse':
      return { icon: 'Eye', label: m['badge.inUse'], tooltip: m['badge.inUse.tip'] };
    case 'phiraIgnores':
      return { icon: 'EyeOff', label: m['badge.phiraIgnores'], tooltip: m['badge.phiraIgnores.tip'] };
    case 'rpe17':
      return { icon: 'Tag', label: m['badge.rpe17'], tooltip: m['badge.rpe17.tip'] };
    case 'editorOnly':
      return { icon: 'PencilLine', label: m['badge.editorOnly'], tooltip: m['badge.editorOnly.tip'] };
    case 'baked':
      return { icon: 'FileDown', label: m['badge.baked'], tooltip: m['badge.baked.tip'] };
    case 'full':
      return { icon: 'Layers', label: m['badge.full'], tooltip: m['badge.full.tip'] };
    case 'problem':
      return { icon: SEVERITY_ICON[severity], label: SEVERITY_LABEL[severity], tooltip: m['badge.problem.tip'] };
    default:
      return assertNever(kind);
  }
}

type Translate = (key: MessageKey) => string;
type TranslateRef = (ref: MessageRef) => string;

/** Visible word and tooltip text of a badge. */
function badgeTexts(
  props: BadgeProps,
  t: Translate,
  tr: TranslateRef,
): { readonly text: string; readonly tip: string } {
  const info = badgeKindInfo(props.kind, props.severity ?? 'warning');
  return {
    text: props.label === undefined ? t(info.label) : tr(props.label),
    tip: props.tooltip === undefined ? t(info.tooltip) : tr(props.tooltip),
  };
}

/** True when the list has a status badge (one without onClick) whose explanation a field should reference. */
export function hasStatusBadges(badges: readonly BadgeProps[] | undefined): boolean {
  return badges?.some((b) => b.onClick === undefined) === true;
}

export function Badge(props: BadgeProps) {
  const t = useT();
  const tr = useTr();
  const severity = props.severity ?? 'warning';
  const info = badgeKindInfo(props.kind, severity);
  const { text, tip } = badgeTexts(props, (key) => t(key), tr);
  const className = cx(s.badge, props.onClick !== undefined && s.clickable);
  const data = {
    'data-kind': props.kind,
    ...(props.kind === 'problem' ? { 'data-severity': severity } : {}),
  };
  const body = (
    <>
      <Icon name={info.icon} size={12} />
      <span className={s.text}>{text}</span>
    </>
  );
  return (
    <Tooltip content={tip}>
      {props.onClick === undefined ? (
        <span className={className} {...data}>
          {body}
        </span>
      ) : (
        <button
          type="button"
          className={className}
          {...data}
          onMouseDown={keepFocusOnPointerDown}
          onClick={() => {
            props.onClick?.();
          }}
        >
          {body}
        </button>
      )}
    </Tooltip>
  );
}

/**
 * A row of badges (inspector fields, section headers); renders nothing for an empty list. With `descriptionId`,
 * it also renders the explanations of its status badges ("Phira ignores: Phira does not read this field...") in
 * a hidden element with that id, for the field's aria-describedby (see `hasStatusBadges`).
 */
export function BadgeList(props: {
  readonly badges: readonly BadgeProps[] | undefined;
  readonly descriptionId?: string | undefined;
}) {
  const t = useT();
  const tr = useTr();
  const badges = props.badges;
  if (badges === undefined || badges.length === 0) return null;
  const described =
    props.descriptionId === undefined
      ? null
      : badges
          .filter((b) => b.onClick === undefined)
          .map((b) => {
            const { text, tip } = badgeTexts(b, (key) => t(key), tr);
            return t(m['badge.described'], { label: text, tip });
          });
  return (
    <span className={s.list}>
      {badges.map((b, i) => (
        <Badge key={`${b.kind}:${String(i)}`} {...b} />
      ))}
      {described !== null && described.length > 0 && (
        <span id={props.descriptionId} hidden>
          {described.join(' ')}
        </span>
      )}
    </span>
  );
}
